import { Link, useLocation } from '@remix-run/react';
import { generateURL, parseURL } from '@archipro-website/top-navigation';
import { convertToProductSuppliersUrl } from '~/modules/url';
import type { FilterSortOption, SearchIndex } from '../type/api';
import { useSearchSorts } from './use-search-sorts';

interface Options {
    category?: SearchIndex;
    sortBy?: FilterSortOption;
    defaultSortBy?: FilterSortOption;
}

export interface SearchSortOptionResult {
    items: SearchSortOptionItem[];
    value?: SearchSortOptionItem;
    defaultSortName?: string;
}

interface SearchSortOptionItem {
    as: typeof Link;
    to: string;
    preventScrollReset: boolean;
    header: string;
}

const useSearchSortOptions = ({
    category,
    sortBy,
    defaultSortBy,
}: Options): SearchSortOptionResult => {
    const { sortOptions, defaultSortName } = useSearchSorts({
        category,
        sortBy,
        defaultSortBy,
    });
    const location = useLocation();
    const { basePath, params, isSuppliersEndpoint } = parseURL(
        location.pathname + location.search,
    );
    delete params['page'];
    const selectedHeader = sortOptions.find(
        (sortOption) => sortOption.selected,
    )?.header;

    const items = sortOptions.map((sortOption) => {
        const to = generateURL(
            isSuppliersEndpoint
                ? convertToProductSuppliersUrl(basePath)
                : basePath,
            {
                ...params,
                sort:
                    sortOption.value && sortOption.value !== defaultSortBy
                        ? [sortOption.value]
                        : [],
            },
        );
        return {
            as: Link,
            to,
            preventScrollReset: true,
            header: sortOption.header,
        };
    });
    const value = items.find((item) => item.header === selectedHeader);

    return { items, value, defaultSortName };
};

export { useSearchSortOptions };
